<template>
  <HSButton
    v-if="organizationSettings.useInventoryTargetingForCreativeApproval"
    v-tooltip="capitalize(tooltip)"
    class-name="actions-btn"
    :is-disabled="disabled"
    :icon-left="markRaw(IconLocation)"
    name="approveCreative"
    variant="success"
    @click="() => emits('promptCreativeFileApprovalForSpecificInventory')">
  </HSButton>
</template>

<script setup lang="ts">
  import { capitalize, computed, markRaw, toRefs } from 'vue';

  import HSButton from '@/common/components/ui/Button/Button.vue';
  import { IconLocation } from '@/common/icons/monochrome';
  import { Creative } from '@/common/models';
  import { mapGetters, mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IProps {
    creative: Creative;
  }

  const props = defineProps<IProps>();

  const { creative } = toRefs(props);

  interface IEmits {
    (e: 'promptCreativeFileApprovalForSpecificInventory'): void;
  }

  const emits = defineEmits<IEmits>();

  const { permissions } = mapGetters();
  const { organizationSettings } = mapOrgAppState();

  /**
   * Used to determine if the approve creative file for specific inventory button should be disabled
   * We should only allow the button to be enabled if the creative file has one or more media owner approvals
   */
  const canCreativeFileBeApprovedForSpecificInventory = computed(() => {
    const creativeFileId = creative.value?.creativeFiles[0]?.id;
    if (creativeFileId == null || creative.value == null) {
      return false;
    }

    const mediaOwnerApprovals = creative.value.mediaOwnerApprovals.filter((x) => x.creativefileID === creativeFileId);
    return mediaOwnerApprovals.length > 0;
  });

  const disabled = computed(
    () => !permissions.value.canWriteCreativemediaownerapproval || !canCreativeFileBeApprovedForSpecificInventory.value,
  );

  const tooltip = computed(() =>
    !canCreativeFileBeApprovedForSpecificInventory.value
      ? i18n.global.t('messages.inventoryApprovalForExternalCreativeFileUnavailable')
      : i18n.global.t('actions.approveForSpecificInventory'),
  );
</script>

<style scoped lang="scss">
  .actions-btn {
    padding: 0.3rem !important;
  }
</style>
